import {
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Divider,
  Box,
  List,
  ListItem,
  ListItemText,
  ThemeProvider,
  Grid,
} from "@mui/material";
import { ROUTES } from "../../../utils/constants";
import React, { useState } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { MilestoneTask } from "../../../api/homeSiteMilestones/types";
import { useHomeSiteTasksObserver } from "../../../reactQuery/homeSites/milestones/mileStoneTasksQuery";
import { useNavigate } from "react-router";
import { theme } from "../../../components/gridToolbarComponents/gridToolbarComponents";
import { DivisionMileStone, DivisionMileStones } from "../../../api/divisionMileStones/types";
import { HomeSitesDataIndex } from "../../../reactQuery/homeSitesDataIndex/types";
import { thinDarkScrollbar } from "../../../utils/scrollbarStyles";

interface TaskMetricsComponentProps {
  filteredHomeSites: HomeSitesDataIndex[] | undefined;
  tasks: MilestoneTask[];
  divisionMilestones: DivisionMileStones;
  navigate: (path: string) => void;
  isLoading: boolean;
}
export const THIS_WEEK = "This Week";
export const NEXT_WEEK = "Next Week";
export const FUTURE_WEEKS = "Future Weeks";
export const ALL_MILESTONES = "All Milestones";
export const NO_DUE_DATE = "Milestones with no Task Due Date";

export type AlignmentType = typeof THIS_WEEK | typeof NEXT_WEEK;

const TaskMetricsComponent: React.FC<TaskMetricsComponentProps> = ({ divisionMilestones, tasks, filteredHomeSites, isLoading}) => {
  const navigate = useNavigate();

  const { metrics: taskMetrics } = useHomeSiteTasksObserver(filteredHomeSites || [], tasks || [], divisionMilestones || [], isLoading);
  const [alignment, setAlignment] = useState<AlignmentType>(THIS_WEEK);

  const handleChange = (event: React.MouseEvent<HTMLElement>, newAlignment: AlignmentType | null) => {
    event.stopPropagation();
    if (newAlignment !== null) {
      setAlignment(newAlignment);
    }
  };

  return (
    <ThemeProvider theme={theme}>
    <Grid sx={{ ...styles.taskroot }} flex={0.45}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }} gap={1} p={1}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography style={{ fontWeight: "bold", marginRight: "8px" }}>
            Milestones with Tasks Due
          </Typography>
          <ToggleButtonGroup value={alignment} exclusive onChange={handleChange} size="small">
            <ToggleButton value={THIS_WEEK} sx={alignment === THIS_WEEK ? styles.drawerItemCurrentStateButton : styles.drawerItemNexStateButton}>
              This Week
            </ToggleButton>
            <ToggleButton value={NEXT_WEEK} sx={alignment === NEXT_WEEK ? styles.drawerItemCurrentStateButton : styles.drawerItemNexStateButton}>
              Next Week
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Button variant="outlined" sx={{ ...styles.drawerItemMilestoneButton }} endIcon={<ArrowForwardIcon />}
          size="small"
          onClick={() =>
            navigate(ROUTES.closings.tasks.route, {
              state: {
                timeFrame: alignment,
              },
            })
          }
          >
          All Milestones
        </Button>
      </Box>
      <Divider style={{width: "100%"}}/>
      <Box sx={{ ...styles.drawer }}>
        <List sx={{ alignItems: "center", marginTop: "8px" }}>
          <ListItem sx={{ backgroundColor: "#F1F5F8", borderRadius: "6px", marginBottom: "8px", height: "54px" }}>
            <Typography sx={{ ...styles.drawerItemsText }}>
              Milestones
            </Typography>
            <Box sx={{ ...styles.drawerItemsCounts }}>
              <Typography sx={{ ...styles.drawerItemsText }}>
                No. of Homesites
              </Typography>
              <Typography sx={{ color: "#C93939", fontWeight: "bold" }}>
                (Overdue)
              </Typography>
            </Box>
          </ListItem>
          <Box sx={ styles.scrollArea }>
            {divisionMilestones?.data
              .filter((value, index, self) => 
                index === self.findIndex(item => item.mileStoneName === value.mileStoneName)
              )
              .sort((a, b) => a.mileStoneOrder - b.mileStoneOrder)
              .map((value: DivisionMileStone) => {
            const normalcount =
              alignment === THIS_WEEK
                ? taskMetrics.thisWeek[value.mileStoneName]?.length || 0
                : taskMetrics.nextWeek[value.mileStoneName]?.length || 0;
            const overDueCount =
              alignment === THIS_WEEK
                ? taskMetrics.thisWeekOverDue[value.mileStoneName]?.length || 0
                : taskMetrics.nextWeekOverDue[value.mileStoneName]?.length || 0;
            return (
              <ListItem divider sx={{ ...styles.listItem }}
                key={value.mileStoneName}
                disableGutters
                onClick={() =>
                  navigate(ROUTES.closings.tasks.route, {
                    state: {
                      timeFrame: alignment,
                      milestone: value.mileStoneName,
                    },
                  })
                }
              >
                <ListItemText primary={`${value.mileStoneName}`} />
                <Box sx={{
                  display: "flex",
                  minWidth: "120px",
                  justifyContent: "space-between",
                  textAlign: "right"
                }}>
                  <Typography sx={{ fontWeight: "bold", textAlign: "center", width: "50%" }}>{normalcount}</Typography>
                  {overDueCount > 0 && (
                    <Typography sx={{ color: "#C93939", fontWeight: "bold", textAlign: "left", width: "50%", }}>
                      ({overDueCount})
                    </Typography>
                  )}
                </Box>
              </ListItem>
            );
          })}
          </Box>
          <ListItem sx={{ ...styles.listItem2 }} onClick={() => navigate(ROUTES.closings.tasks.route, { state: { timeFrame: alignment, milestone: NO_DUE_DATE } })}>
            <Box sx={{ ...styles.listItemElements }}>
              <CalendarMonthIcon sx={{ color: "#C58026" }} />
              <Typography sx={{ color:  "#2A3F4D" }}>
                {taskMetrics.noDueDateCount} Milestones with no Tasks Due.
              </Typography>
            </Box>
          </ListItem>
        </List>
      </Box>
    </Grid>
    </ThemeProvider>
  );
};

export default TaskMetricsComponent;

const styles = {
  taskroot: {
    display: "flex",
    flexDirection: "column",
    border: "1px solid #E7ECF0 ",
    borderRadius: "6px",
    background: "#FFFFFF",
    padding: "16px",
  },
  taskDrawerRoot: {
    gap: 2,
    flexWrap: "wrap",
    display: "flex",
    alignItems: "center",
  },
  drawer: {
    height: "100%",
  },
  drawerItems: {
    display: "flex",
    borderRadius: "4px",
    backgroundColor: "#FAFBFC",
    gap: "8px",
    alignItems: "center",
  },
  drawerItemsCounts: {
    minWidth: "120px",
    marginLeft: "auto",
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  drawerItemsText: {
    fontWeight: "bold",
    color: "#1F75BB",
  },
  drawerItemCurrentStateButton: {
    backgroundColor: "#FFFFFF !important",
    color: "#1F75BB !important",
    textTransform: "none",
    padding: "6px 12px",
    minWidth: "100px",
    boxShadow: "1px 1px 8px 0px #3E546324",
    fontWeight: "bold",
  },
  drawerItemNexStateButton: {
    backgroundColor: "#E7ECF0 !important",
    color: "#1C2B36 !important",
    border: "1px solid transparent !important",
    textTransform: "none",
    padding: "6px 12px",
    minWidth: "100px",
    fontWeight: "bold",
  },

  drawerItemMilestoneButton: {
    textTransform: "none",
    fontWeight: "bold",
    border: "2px solid #005DAA",
    "&:hover": {
      border: "2px solid #005DAA",
    },
  },
  listItem: {
    padding: "16px 12px 16px 12px",
    height: "54px",
    backgroundColor: "#F1F5F8",
    border: "none !important",
    gap: "8px",
    marginBottom: "8px",
    alignItems: "center",
    color: "#1C2B36",
    cursor: "pointer",
    justifyItems: "space-between",
    borderRadius: "6px",
  },
  listItem2: {
    padding: "16px 12px 16px 12px",
    height: "54px",
    backgroundColor: "#FFF8E1",
    gap: "8px",
    marginTop: "8px",
    alignItems: "center",
    cursor: "pointer",
  },
  listItemElements: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
  },
  scrollArea: {
    maxHeight: 450,
    overflowY: "auto",
    ...thinDarkScrollbar
  }
};
